import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import axios from "axios";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ApiTest: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [method, setMethod] = useState("GET");
  const [url, setUrl] = useState("/api/orders");
  const [headers, setHeaders] = useState(`{
  "Content-Type": "application/json",
  "Authorization": "Bearer YOUR_TOKEN"
}`);
  const [body, setBody] = useState("{}");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);

  const apiExamples = [
    {
      name: "Get Orders",
      method: "GET",
      url: "/api/orders",
      headers: '{ "Authorization": "Bearer YOUR_TOKEN" }',
      body: "",
    },
    {
      name: "Create Order",
      method: "POST",
      url: "/api/orders",
      headers:
        '{ "Content-Type": "application/json", "Authorization": "Bearer YOUR_TOKEN" }',
      body: JSON.stringify(
        {
          invId: "TEST_123",
          type: "DEPOSIT",
          amount: 1000,
          currency: "RUB",
          bank: "sberbank",
        },
        null,
        2
      ),
    },
    {
      name: "Merchant API - Create Order",
      method: "POST",
      url: "/api/merchant/create-order",
      headers:
        '{ "Content-Type": "application/json", "X-API-Key": "YOUR_API_KEY" }',
      body: JSON.stringify(
        {
          invId: "MERCHANT_123",
          amount: 1000,
          currency: "RUB",
          bank: "sberbank",
        },
        null,
        2
      ),
    },
    {
      name: "Create Appeal",
      method: "POST",
      url: "/api/appeals",
      headers:
        '{ "Content-Type": "application/json", "Authorization": "Bearer YOUR_TOKEN" }',
      body: JSON.stringify(
        {
          orderId: "ORDER_ID",
          reason: "Payment not received",
          comment: "Customer claims payment was made",
          requireClientInvoice: true,
        },
        null,
        2
      ),
    },
    {
      name: "Provider Integration - Create Order",
      method: "POST",
      url: "/api/integration/orders",
      headers:
        '{ "Content-Type": "application/json", "Authorization": "Bearer YOUR_TOKEN" }',
      body: JSON.stringify(
        {
          amount: 1000,
          currency: "RUB",
          bank: "sberbank",
        },
        null,
        2
      ),
    },
    {
      name: "Get Exchange Rates",
      method: "GET",
      url: "/api/exchange-rates",
      headers: '{ "Authorization": "Bearer YOUR_TOKEN" }',
      body: "",
    },
  ];

  const sendRequest = async () => {
    setLoading(true);
    setResponse("");

    try {
      const parsedHeaders = JSON.parse(headers);
      const parsedBody = body ? JSON.parse(body) : undefined;

      const config = {
        method,
        url: url.startsWith("http") ? url : `http://localhost:4000${url}`,
        headers: parsedHeaders,
        data: method !== "GET" ? parsedBody : undefined,
      };

      const result = await axios(config);
      setResponse(JSON.stringify(result.data, null, 2));
    } catch (error: any) {
      if (error.response) {
        setResponse(
          JSON.stringify(
            {
              error: error.message,
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data,
            },
            null,
            2
          )
        );
      } else {
        setResponse(
          JSON.stringify(
            {
              error: error.message,
            },
            null,
            2
          )
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const loadExample = (example: any) => {
    setMethod(example.method);
    setUrl(example.url);
    setHeaders(example.headers);
    setBody(example.body);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const curlCommand = `curl -X ${method} \\
  '${url.startsWith("http") ? url : `http://localhost:4000${url}`}' \\
  ${Object.entries(JSON.parse(headers || "{}"))
    .map(([key, value]) => `-H '${key}: ${value}'`)
    .join(" \\\n  ")}${method !== "GET" && body ? ` \\\n  -d '${body}'` : ""}`;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        API Testing Console
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
              <Tab label="Request" />
              <Tab label="Examples" />
              <Tab label="cURL" />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Method</InputLabel>
                  <Select
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                  >
                    <MenuItem value="GET">GET</MenuItem>
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="PUT">PUT</MenuItem>
                    <MenuItem value="PATCH">PATCH</MenuItem>
                    <MenuItem value="DELETE">DELETE</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="URL"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="/api/orders"
                />
              </Box>

              <TextField
                fullWidth
                multiline
                rows={6}
                label="Headers (JSON)"
                value={headers}
                onChange={(e) => setHeaders(e.target.value)}
                margin="normal"
              />

              {method !== "GET" && (
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="Body (JSON)"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  margin="normal"
                />
              )}

              <Button
                variant="contained"
                fullWidth
                onClick={sendRequest}
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? "Sending..." : "Send Request"}
              </Button>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Typography variant="h6" gutterBottom>
                API Examples
              </Typography>
              <Grid container spacing={2}>
                {apiExamples.map((example, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          {example.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {example.method} {example.url}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => loadExample(example)}
                          sx={{ mt: 1 }}
                        >
                          Load Example
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Typography variant="h6" gutterBottom>
                cURL Command
              </Typography>
              <Paper sx={{ p: 2, backgroundColor: "background.default" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                  }}
                >
                  <pre style={{ margin: 0, flex: 1 }}>{curlCommand}</pre>
                  <Tooltip title="Copy cURL command">
                    <IconButton onClick={() => copyToClipboard(curlCommand)}>
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
              <Alert severity="info" sx={{ mt: 2 }}>
                You can copy this cURL command and run it in your terminal.
              </Alert>
            </TabPanel>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Response
            </Typography>
            {response ? (
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: "background.default",
                  maxHeight: 500,
                  overflow: "auto",
                }}
              >
                <pre style={{ margin: 0 }}>{response}</pre>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No response yet. Send a request to see the result.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Alert severity="warning" sx={{ mt: 3 }}>
        <Typography variant="body2">
          Note: Make sure you have valid authentication tokens and API keys
          before testing. Some endpoints require specific roles or permissions.
        </Typography>
      </Alert>
    </Box>
  );
};

export default ApiTest;
